interface CampaignData {
  id: string;
  campaign_id: string;
  country_code: string;
  language_code: string;
  culture?: string | null;
  free_episode_price_coins: number;
  fallback_language_code?: string | null;
  free_episodes_count?: number | null;
  revenue_cat_enabled_app?: boolean;
  revenue_cat_enabled_web?: boolean;
  dcb_enabled_web?: boolean;
}

class CampaignCacheService {
  private cache: Map<string, CampaignData> = new Map();
  private isInitialized: boolean = false;

  get(campaignCountriesLanguagesId: string): CampaignData | null {
    const data = this.cache.get(campaignCountriesLanguagesId);
    if (data) {
      return data;
    }
    return null;
  }

  getCampaignId(campaignCountriesLanguagesId: string): string | null {
    const data = this.get(campaignCountriesLanguagesId);
    return data?.campaign_id || null;
  }

  getEpisodePrice(campaignCountriesLanguagesId: string): number {
    const data = this.get(campaignCountriesLanguagesId);
    return data?.free_episode_price_coins || 0;
  }

  getFreeEpisodesCount(campaignCountriesLanguagesId: string): number {
    const data = this.get(campaignCountriesLanguagesId);
    return data?.free_episodes_count ?? 3;
  }

  getLanguageCode(campaignCountriesLanguagesId: string): string {
    const data = this.get(campaignCountriesLanguagesId);
    return data?.language_code || 'en';
  }

  getFallbackLanguages(campaignCountriesLanguagesId: string): string[] {
    const data = this.get(campaignCountriesLanguagesId);
    if (!data?.fallback_language_code) {
      return [];
    }
    // Parse comma-separated string into array and trim whitespace
    return data.fallback_language_code.split(',').map(lang => lang.trim()).filter(lang => lang.length > 0);
  }

  set(data: CampaignData): void {
    this.cache.set(data.id, data);
  }

  has(campaignCountriesLanguagesId: string): boolean {
    return this.cache.has(campaignCountriesLanguagesId);
  }

  clear(): void {
    this.cache.clear();
    this.isInitialized = false;
  }

  markInitialized(): void {
    this.isInitialized = true;
  }

  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  getCacheStats(): { size: number; isInitialized: boolean; keys: string[] } {
    return {
      size: this.cache.size,
      isInitialized: this.isInitialized,
      keys: Array.from(this.cache.keys())
    };
  }
}

export const campaignCache = new CampaignCacheService();
export type { CampaignData };
